/**
 * EVO — yuqori panel.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ TARTIB: QAYERDAMAN → QANCHA → QANDAY → KIM                               ║
 * ║                                                                          ║
 * ║ Chapda sahifa nomi. Avval u YO'Q edi va bu jiddiy kamchilik: yon menyu   ║
 * ║ guruhlari yig'ilgandan keyin faol band ko'rinmay qolishi mumkin, ya'ni   ║
 * ║ foydalanuvchi qayerda turganini ekrandan bilolmasdi. Sarlavha yon menyu  ║
 * ║ bilan AYNAN bir manbadan (`findActiveMenuItem`) olinadi — ikki joyda     ║
 * ║ ikki xil nom ko'rsatish chalg'itadi.                                     ║
 * ║                                                                          ║
 * ║ O'ngda esa bitta ixcham qator: FILIAL → KURS → TIL → FOYDALANUVCHI.      ║
 * ║ Tartib ma'noga qarab: QAYERDA ishlayapman → QANCHA (kunlik kurs) →       ║
 * ║ QANDAY o'qiyman (til) → KIM man.                                         ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN HAMMASI «CHIP» GA AYLANDI                                     │
 * │                                                                          │
 * │ Egasining uchta mulohazasi bir xil sababdan edi:                         │
 * │   • «kurs — katta, uzun yozuv, oddiy `$ 12 800` yetadi»                  │
 * │   • «yuan kerak emas, bazada bo'lsa bo'ldi»                              │
 * │   • «til katta joy, uz/ru/en yetadi»                                     │
 * │                                                                          │
 * │ Panelning o'ng tomonida ~640 px band edi va uning katta qismi HAR KUNI   │
 * │ BIR XIL turadigan matn: «Kunlik kurs», «1 USD = …», «O'zbekcha». Bunday  │
 * │ matn ikkinchi kundan boshlab o'qilmaydi, lekin sahifa sarlavhasini       │
 * │ siqib chiqarardi — 1280 px li noutbukda sarlavha qirqilib qolardi.       │
 * │                                                                          │
 * │ Endi har element BIR XIL balandlikdagi (`h-8`) chip: filial, kurs, til,  │
 * │ foydalanuvchi. Bir xil balandlik tasodif emas — turli balandlikdagi      │
 * │ elementlar qatori «yig'ilmagan» ko'rinadi va ko'z qaysi biri muhim       │
 * │ ekanini noto'g'ri o'qiydi.                                               │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ TOR EKRANDA NIMA QISQARADI (va nima QISQARMAYDI)                         │
 * │                                                                          │
 * │ 1280 px: hammasi BITTA qatorda, hech narsa o'ralmaydi va kesilmaydi.     │
 * │                                                                          │
 * │ 390 px (telefon): filial yorlig'i va foydalanuvchi ismi yashiriladi —    │
 * │ ikkalasi ham «men bilaman» toifasidagi ma'lumot va ular yo'qolgani       │
 * │ bilan ish to'xtamaydi. KURS esa QOLADI: u aynan tashqarida, telefon      │
 * │ bilan ishlayotgan sotuvchiga kerak.                                      │
 * │                                                                          │
 * │ Sarlavha `basis-40 shrink-0` — ya'ni u 160 px dan pastga TUSHMAYDI va    │
 * │ joy yetmasa o'ng blok o'zi pastki qatorga o'tadi. Aks holda sarlavha     │
 * │ nol kenglikkacha siqilib, telefonda «S…» bo'lib qolardi — bu esa         │
 * │ sarlavhaning butun ma'nosini yo'q qiladi.                                │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useRouter, useRouterState } from '@tanstack/react-router';
import { filterMenu, findActiveMenuItem, MENU } from '../../config/menu';
import { useAuth, useCan } from '../../lib/auth';
import type { TranslationKey } from '../../lib/i18n';
import { useI18n } from '../../lib/i18n-context';
import { clearRecents } from '../../lib/search';
import { CommandPalette } from '../command-palette';
import { ShortcutsHelp } from '../shortcuts-help';
import { NotificationBell } from './notification-bell';
import { FxRateWidget } from './fx-rate-widget';
import { LangSwitch } from './lang-switch';
import { ThemeSwitch } from './theme-switch';
import { BranchIndicator, clearActiveBranch } from './branch-switcher';
import { usePageTitleValue } from './page-title';

/**
 * Rol kodini odam o'qiydigan nomga aylantiradi.
 *
 * Noma'lum kod uchun KODNING O'ZI qaytariladi, `undefined` emas: yangi rol
 * qo'shilib tarjima unutilsa, foydalanuvchi bo'sh joy emas, hech bo'lmasa
 * `counter` deb ko'radi va nima bo'layotganini taxmin qila oladi.
 */
const ROLE_KEYS: Record<string, TranslationKey> = {
  admin: 'role.label.admin',
  manager: 'role.label.manager',
  finance: 'role.label.finance',
  warehouse: 'role.label.warehouse',
  sales: 'role.label.sales',
};

function roleLabel(role: string, t: (key: TranslationKey) => string): string {
  const key = ROLE_KEYS[role];
  return key === undefined ? role : t(key);
}

/**
 * Ism bosh harflari — avatar uchun.
 *
 * Rasm YO'Q va bo'lmaydi ham: ERP da xodim surati saqlanmaydi va uni
 * qo'shish kunlik ishga hech narsa bermaydi. Bosh harflar esa bitta savolga
 * javob beradi — «men kim bo'lib kirganman?» — va buni 32 px da qiladi.
 *
 * Ikki so'zdan ikki harf, bitta so'zdan bitta: «B. O.» dagi nuqta va
 * bo'shliqlar 32 px doiraga sig'maydi, shuning uchun faqat harflar olinadi.
 */
function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]![0] ?? '';
  const second = parts.length > 1 ? (parts[1]![0] ?? '') : '';
  return (first + second).toUpperCase();
}

export function Topbar({ onMenuClick }: { readonly onMenuClick?: () => void } = {}): ReactNode {
  const { user, logout, roleCodes } = useAuth();
  const { t } = useI18n();
  const can = useCan();

  const router = useRouter();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const groups = useMemo(() => filterMenu(MENU, can), [can]);
  const active = useMemo(() => findActiveMenuItem(groups, pathname), [groups, pathname]);

  /*
   * Ekran o'z sarlavhasini bergan bo'lsa — U USTUN.
   *
   * Sabab: menyu faqat bo'lim nomini biladi («Sotuv buyurtmalari»), ekran
   * esa aynan nima ochilganini («SO-2026-00042»). Hujjat kartochkasida
   * ikkinchisi kerak. Menyu nomi kichik qatorga tushadi va kontekst
   * yo'qolmaydi.
   */
  const declared = usePageTitleValue();
  const kicker = declared?.kicker ?? (active === null ? null : t(active.group.labelKey));
  const heading = declared?.title ?? (active === null ? null : t(active.item.labelKey));

  /*
   * Buyruq paneli holati SHU YERDA turadi.
   *
   * Panelning O'ZI `AppShell` da emas, yuqori panelda joylashdi: uni ochadigan
   * tugma ham shu yerda va holat egasi bitta bo'lishi kerak. Yuqori panel esa
   * `AppShell` ichida HAR ekranda render bo'ladi, ya'ni Ctrl+K istalgan
   * joydan ishlaydi — joylashuv qamrovni toraytirmaydi.
   */
  const [paletteOpen, setPaletteOpen] = useState(false);

  return (
    /*
     * `min-h-16` — yon menyudagi brend blokining balandligi bilan AYNAN
     * bir xil. Usiz sarlavhasiz ekranlarda panel 4 px pastroq bo'lardi va
     * chapdagi chegara chizig'i o'ngdagi chiziq bilan bir chiziqqa
     * tushmasdi. Bu «kichik» farq har ekranda ko'rinadi.
     */
    /*
     * ╔══════════════════════════════════════════════════════════════════╗
     * ║ YUQORI PANEL ENDI KORPUSNING BIR QISMI — OQ SIRT EMAS            ║
     * ║                                                                  ║
     * ║ EGASINING TALABI: «tepada ham brend rangidan foydalan».           ║
     * ║                                                                  ║
     * ║ Ilgari bu yerda `bg-surface` (oq) turardi va ramka YARIM edi:     ║
     * ║ chapda to'q panel, tepada esa oq yuza va ish maydonidan uni faqat ║
     * ║ ingichka kulrang chiziq ajratardi. Ekranga qaraganda ikkita       ║
     * ║ boshqa-boshqa element ko'rinardi, yaxlit qobiq emas.              ║
     * ║                                                                  ║
     * ║ Endi ikkalasi bitta `evo-chrome` yuzasi. Ichkaridagi tugmalar     ║
     * ║ ham `chrome-*` tokenlariga o'tdi — usiz kulrang matn yashil fonda ║
     * ║ o'qilmay qolardi.                                                 ║
     * ║                                                                  ║
     * ║ Pastdagi chegara QOLDI (`border-chrome-line`): usiz panel bilan   ║
     * ║ yon menyu bir-biriga quyilib ketardi va burchakda ularning        ║
     * ║ chegarasi yo'qolardi.                                             ║
     * ╚══════════════════════════════════════════════════════════════════╝
     */
    <header className="evo-chrome flex min-h-16 flex-wrap items-center gap-x-3 gap-y-2 border-b border-chrome-line px-3 py-2 sm:px-5">
      {/*
        Menyu tugmasi — FAQAT telefonda (`lg:hidden`).

        Katta ekranda menyu doim ko'rinib turadi va tugma faqat chalg'itardi.
        Ikonka SVG bilan shu yerda yozilgan: `nav-icons.tsx` menyu GURUHLARI
        uchun, bu esa boshqaruv elementi — ikkalasini bir joyga qo'shish
        keyin «qaysi ikonka qayerdan?» degan savol tug'dirardi.
      */}
      {onMenuClick !== undefined && (
        <button
          type="button"
          onClick={onMenuClick}
          aria-label={t('nav.menu')}
          className="-ml-1 flex size-9 shrink-0 items-center justify-center rounded-lg text-chrome-muted transition-colors hover:bg-chrome-2 hover:text-chrome-ink motion-reduce:transition-none lg:hidden"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="size-5" aria-hidden="true">
            <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
          </svg>
        </button>
      )}

      {/*
        QIDIRUV tugmasi — menyu tugmasi YONIDA va BARCHA ekranlarda.

        Ctrl+K klaviaturasi bor odam uchun yetarli, lekin telefonda klaviatura
        YO'Q: tugmasiz panel mobil qurilmada umuman ochilmasdi. Shuning uchun
        u `lg:hidden` EMAS — katta ekranda ham ko'rinadi va yangi xodimga
        funksiyaning borligini bir marta ko'rsatadi (tugma yozuvida Ctrl+K
        bor, ya'ni u o'zi o'rgatadi).
      */}
      <button
        type="button"
        onClick={() => setPaletteOpen(true)}
        aria-label={t('search.commandOpen')}
        title={t('search.commandOpen')}
        className="-ml-1 flex size-9 shrink-0 items-center justify-center rounded-lg text-chrome-muted transition-colors hover:bg-chrome-2 hover:text-chrome-ink motion-reduce:transition-none"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="size-5" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-4.2-4.2" strokeLinecap="round" />
        </svg>
      </button>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />

      {/*
        Tugmalar yordami — `CommandPalette` YONIDA va shu sababdan.

        Ikkalasi ham butun ilova ustidan ishlaydigan, o'z holatini o'zi
        boshqaradigan oyna. Yuqori panel har ekranda chiziladi, ya'ni «?»
        istalgan joyda ishlaydi. Uni `AppShell` ga qo'yish ham mumkin edi,
        lekin o'shanda ikki qardosh oyna ikki xil faylda turardi.
      */}
      <ShortcutsHelp />

      {/*
        ╔══════════════════════════════════════════════════════════════════╗
        ║ «ORQAGA» — HAR EKRANDA, BITTA JOYDA                              ║
        ║                                                                  ║
        ║ EGASINING TALABI: «dasturga orqaga tugmasi yo'q, luboy joyga     ║
        ║ kirganda orqaga bo'lishi kerak».                                  ║
        ║                                                                  ║
        ║ Ilgari «Orqaga» BOR edi, lekin faqat sakkizta kartochkada        ║
        ║ (mahsulot, mijoz, buyurtma...) va har birida O'Z joyida —        ║
        ║ ba'zisida sarlavha ostida, ba'zisida amallar qatorida. Qolgan    ║
        ║ ~90 ekranda umuman yo'q edi. Ya'ni foydalanuvchi tugmani         ║
        ║ QIDIRISHI kerak edi va ko'pincha topmasdi: brauzerning o'z       ║
        ║ tugmasi esa to'liq ekran rejimida va ilova sifatida              ║
        ║ o'rnatilganda (PWA) ko'rinmaydi.                                 ║
        ║                                                                  ║
        ║ Endi u KARKASDA: joyi hech qachon o'zgarmaydi va yangi ekran     ║
        ║ qo'shgan dasturchi uni unuta olmaydi.                            ║
        ╚══════════════════════════════════════════════════════════════════╝

        ┌──────────────────────────────────────────────────────────────────┐
        │ BOSH SAHIFADA CHIZILMAYDI                                        │
        │                                                                  │
        │ Bosh sahifa — zanjirning boshi: u yerdan «orqaga» yo tashqariga  │
        │ (boshqa sayt), yo hech qayerga olib boradi. Ishlamaydigan tugma  │
        │ esa ishonchni yo'qotadi — birinchi bosishdayoq.                  │
        │                                                                  │
        │ `router.history.back()` — `navigate({ to: '/' })` EMAS. Ikkinchi │
        │ variant «orqaga» emas, «bosh sahifaga» bo'lardi: hisobotdan      │
        │ kartochkaga kirgan odam qaytganda ro'yxatning O'SHA joyiga,      │
        │ o'sha filtri va sahifasi bilan tushishi kerak — ular manzilda    │
        │ (`search params`) saqlangan va tarix ularni tiklaydi.            │
        └──────────────────────────────────────────────────────────────────┘
      */}
      {pathname !== '/' && (
        <button
          type="button"
          onClick={() => router.history.back()}
          aria-label={t('common.back')}
          title={t('common.back')}
          className="flex h-9 shrink-0 items-center justify-center gap-1 rounded-lg px-2 text-chrome-muted transition-colors hover:bg-chrome-2 hover:text-chrome-ink motion-reduce:transition-none"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="size-5" aria-hidden="true">
            <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {/*
            ┌────────────────────────────────────────────────────────────┐
            │ NOMI YOZILADI — «EVO'DAN SO'RASH» DAN OLINGAN SABOQ        │
            │                                                            │
            │ O'sha tugma ham yozuvsiz, faqat belgi bilan turgan edi va  │
            │ egasi uni ko'rmadi. Yozuvsiz belgi ALLAQACHON ma'lum tugma │
            │ uchun ishlaydi (qidiruv — lupa, chiqish — eshik), yangi    │
            │ narsa uchun esa yo'q.                                       │
            │                                                            │
            │ `sm` chegarasi: telefonda faqat strelka qoladi — u yerda   │
            │ joy haqiqatan yo'q va strelkaning ma'nosi telefonda        │
            │ allaqachon o'rganilgan.                                     │
            └────────────────────────────────────────────────────────────┘
          */}
          <span className="hidden whitespace-nowrap text-sm font-medium sm:inline">
            {t('common.back')}
          </span>
        </button>
      )}

      {/*
        ╔══════════════════════════════════════════════════════════════════╗
        ║ CHAP: QAYERDAMAN — SARLAVHAGA POL QO'YILDI                      ║
        ║                                                                  ║
        ║ Bu yerda `min-w-0 shrink-0 grow basis-40 md:shrink md:basis-0`   ║
        ║ turardi va izohida «katta ekranda sarlavhaga KO'PROQ joy         ║
        ║ qoladi» deb yozilgan edi. Aslida teskarisi bo'lardi.             ║
        ║                                                                  ║
        ║ `md:basis-0` + `md:shrink` + `min-w-0` uchtasi birga «bu blokni  ║
        ║ NOLGACHA siqish mumkin» degani. O'ng tomondagi bloklarning       ║
        ║ deyarli hammasida esa `shrink-0` bor — ya'ni siqiladigan yagona  ║
        ║ element sarlavhaning O'ZI edi.                                   ║
        ║                                                                  ║
        ║ Jonli ko'rindi (1440 px, admin roli): «Mahsulotlar» sarlavhasi   ║
        ║ ekranda «M…» bo'lib qoldi, ustidagi bo'lim nomi esa «MA'…».      ║
        ║ Ya'ni «qayerdaman?» degan savolga javob beradigan yagona element ║
        ║ aynan eng keng ekranda o'qilmas edi.                             ║
        ║                                                                  ║
        ║ Yechim — POL: `min-w` flex siqilishini TO'XTATADI (element o'z   ║
        ║ `min-width` idan pastga tusha olmaydi), `truncate` esa o'sha pol ║
        ║ ichida ishlayveradi. Ekran kengaygan sari pol ham kengayadi.     ║
        ║                                                                  ║
        ║ O'ng bloklardan birortasini olib tashlash ham mumkin edi         ║
        ║ (kurs, filial) — RAD ETILDI: ularning har biri kuniga o'nlab     ║
        ║ marta qaraladi, sarlavha esa ular bilan RAQOBATLASHMASLIGI       ║
        ║ kerak, o'rnini bo'shatishi emas.                                 ║
        ╚══════════════════════════════════════════════════════════════════╝
      */}
      <div className="min-w-28 shrink grow basis-40 md:min-w-44 md:basis-0 xl:min-w-52">
        {heading !== null && (
          <>
            {/*
             * Kichik qator sarlavha USTIDA: «Ombor › Qoldiq» ni bitta qatorga
             * sig'dirish uzun bo'lim nomlarida joyni yeydi, ikki qator esa
             * balandlikni oshirmaydi (sarlavha baribir ikki qatorli blok).
             */}
            {/*
             * ┌────────────────────────────────────────────────────────────┐
             * │ `uppercase` OLIB TASHLANDI — O'Z QOIDAMIZGA ZID EDI        │
             * │                                                            │
             * │ Jadval ustun sarlavhalaridan bosh harflar aynan shu sabab  │
             * │ bilan olib tashlangan edi: o'zbekcha nomlar uzun va bosh   │
             * │ harflar ko'z skanerda ishlatadigan so'z SILUETINI yo'q     │
             * │ qiladi («Mahsulotlar» → «MAHSULOTLAR» — barcha harflar bir │
             * │ balandlikda, ya'ni so'z shakli yo'qoladi).                  │
             * │                                                            │
             * │ Bu qator o'sha o'tishda o'tkazib yuborilgan: u ham uzun    │
             * │ bo'lim nomlarini ko'rsatadi («Ta'minot va xaridlar»), ham  │
             * │ `text-xs` da turadi — ya'ni bosh harflar eng ko'p zarar    │
             * │ qiladigan joy. `tracking-wide` qoldirildi: u kichik matnni │
             * │ sarlavhadan ajratadi va siluetga tegmaydi.                  │
             * └────────────────────────────────────────────────────────────┘
             */}
            {kicker !== null && (
              <p className="truncate text-xs leading-tight tracking-wide text-chrome-muted">
                {kicker}
              </p>
            )}
            <h1 className="truncate text-xl leading-tight font-semibold tracking-tight text-chrome-ink">{heading}</h1>
          </>
        )}
      </div>

      {/*
        ╔══════════════════════════════════════════════════════════════════╗
        ║ O'NG TOMON: FILIAL · KURS · TIL · FOYDALANUVCHI                  ║
        ║                                                                  ║
        ║ Blok BUTUNLIGICHA `shrink-0`: uning ichidagi hech bir element     ║
        ║ qisqarmaydi va kesilmaydi. Joy yetmasa BLOKNING O'ZI pastki      ║
        ║ qatorga o'tadi (telefon) — bu «hammasi biroz kichrayadi» dan     ║
        ║ yaxshiroq, chunki yarim ko'rinadigan kurs raqami noto'g'ri       ║
        ║ o'qilishi mumkin, pastki qatordagi to'liq raqam esa yo'q.        ║
        ║                                                                  ║
        ║ `ml-auto` o'ralganda ham ishlaydi: pastki qatorda blok yolg'iz   ║
        ║ qoladi va baribir o'ngga tekislanadi.                            ║
        ╚══════════════════════════════════════════════════════════════════╝
      */}
      {/*
        ⚠️ `gap-2` EDI (8 px). Bu blokda sakkizta element bor va ularning
        yarmi yozuvsiz belgi — 8 px oraliqda ular BITTA uzun asboblar
        chizig'iga qo'shilib ketardi, ya'ni ko'z ularni alohida tugma deb
        emas, naqsh deb o'qiydi. 10 px yetadi: elementlar ajraladi, qator
        esa (o'lchandi, 1440 px, admin roli) baribir bitta qatorda qoladi.
      */}
      <div className="ml-auto flex shrink-0 items-center gap-2.5">
        {/*
          ╔══════════════════════════════════════════════════════════════════╗
          ║ «EVO'DAN SO'RASH» — ASBOB, BO'LIM EMAS                          ║
          ║                                                                  ║
          ║ Egasi uni yon menyudan olib tashlashni so'radi (sabab            ║
          ║ `config/menu.ts` da). Bu yerda u qatorning BOSHIDA turadi va bu  ║
          ║ tasodif emas: o'ng blokning qolgani — HOLAT (qaysi filial,       ║
          ║ qanday kurs, qaysi til, kim man), bu esa AMAL. Amalni holat      ║
          ║ zanjirining o'rtasiga suqib qo'yish o'sha zanjirni buzardi.      ║
          ║                                                                  ║
          ║ Ruxsat menyudagi bilan AYNAN bir xil (`ai.ask`): u yo'q odamda   ║
          ║ tugma umuman chizilmaydi. AI sarfi pul turadi va uni hamma       ║
          ║ ochavermaydi.                                                    ║
          ║                                                                  ║
          ║ Nomi faqat `aria-label`/`title` da — qidiruv va chiqish          ║
          ║ tugmalari bilan bir xil qoida: kuniga bir necha marta bosiladigan║
          ║ tugma uchun 100 px matn sahifa sarlavhasini siqib chiqarardi.    ║
          ╚══════════════════════════════════════════════════════════════════╝
        */}
        {can('ai.ask') && (
          <Link
            to="/ai"
            aria-label={t('nav.aiAsk')}
            title={t('nav.aiAsk')}
            className={`flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg px-2 transition-colors motion-reduce:transition-none ${
              pathname === '/ai'
                ? 'bg-chrome-2 text-chrome-accent'
                : 'text-chrome-muted hover:bg-chrome-2 hover:text-chrome-ink'
            }`}
          >
            {/*
              Uchqun — AI ning keng tarqalgan belgisi. Katta uchqun yonida
              kichigi bor: bitta to'rt burchakli yulduz «sevimlilar» deb ham
              o'qilishi mumkin, ikkitasi esa bunday chalkashlik qoldirmaydi.
            */}
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              className="size-5"
              aria-hidden="true"
            >
              <path
                d="M13 3l1.7 4.6L19.3 9.3 14.7 11 13 15.6 11.3 11 6.7 9.3l4.6-1.7z"
                strokeLinejoin="round"
              />
              <path d="M6 15.5l.8 2.2 2.2.8-2.2.8L6 21.5l-.8-2.2-2.2-.8 2.2-.8z" strokeLinejoin="round" />
            </svg>
            {/*
              ╔══════════════════════════════════════════════════════════════╗
              ║ NOM QAYTARILDI — EGASI TUGMANI TOPA OLMADI                  ║
              ║                                                              ║
              ║ Avval bu yerda faqat uchqun belgisi turardi, nomi esa       ║
              ║ `title` da yashiringan edi (sabab: 100 px matn sahifa       ║
              ║ sarlavhasini siqib chiqarardi).                              ║
              ║                                                              ║
              ║ Amalda esa egasi «ko'rmadim» dedi — ya'ni belgi o'z-o'zicha ║
              ║ hech narsa aytmadi. Yozuvsiz belgi faqat MA'LUM tugma uchun ║
              ║ ishlaydi (qidiruv, chiqish), yangi narsa uchun emas.        ║
              ║                                                              ║
              ║ Siqilish muammosi `lg` chegarasi bilan yechildi: kichik     ║
              ║ ekranda faqat belgi qoladi (u yerda joy haqiqatan yo'q),    ║
              ║ kompyuterda esa nom ko'rinadi — rahbar aynan o'sha yerda    ║
              ║ ishlaydi.                                                    ║
              ╚══════════════════════════════════════════════════════════════╝
            */}
            {/*
              ┌──────────────────────────────────────────────────────────────┐
              │ `2xl` DAN `xl` GA TUSHIRILDI — O'LCHOV QAYTA QILINDI         │
              │                                                              │
              │ `2xl` (1536 px) o'sha paytdagi o'lchovga to'g'ri edi: o'ng   │
              │ blok 915 px egallardi, chunki foydalanuvchi ismi va roli     │
              │ ham shu qatorda turardi. Ular yashirilgandan keyin o'lchov   │
              │ QAYTA QILINMADI va yozuv keraksiz baland chegarada qolib     │
              │ ketdi.                                                       │
              │                                                              │
              │ Bugungi o'lchov (admin roli, ismsiz): o'ng blok ~592 px,     │
              │ chap tugmalar ~186, sarlavha poli 208 — 1280 px da bu yozuv  │
              │ bilan birga 1121 px, ya'ni 159 px zaxira bor.                │
              │                                                              │
              │ Nima uchun aynan BU yozuv qaytarildi, ism emas: egasi bu     │
              │ tugmani «ko'rmadim» dedi, ya'ni u AMAL va topilishi kerak.   │
              │ Ism esa PASSIV ma'lumot — foydalanuvchi kim bo'lib           │
              │ kirganini biladi va avatar bilan `title` unga yetadi.        │
              │                                                              │
              │ 1280 px dan tor ekranda ikonka HAR DOIM qoladi va havola     │
              │ ishlayveradi; yo'qoladigan narsa faqat yozuv.                │
              └──────────────────────────────────────────────────────────────┘
            */}
            <span className="hidden whitespace-nowrap text-sm font-medium xl:inline">
              {t('nav.aiAsk')}
            </span>
          </Link>
        )}

        <BranchIndicator />
        <FxRateWidget />
        {/*
          * QO'NG'IROQ til almashtirgichdan OLDIN: u kuniga o'nlab marta
          * qaraladigan narsa, til esa bir marta tanlanadi va unutiladi.
          */}
        <NotificationBell />
        {/*
          * Mavzu tugmasi til almashtirgichdan OLDIN va SABABI bir xil emas:
          * til bir marta tanlanadi, mavzu esa kun davomida almashishi mumkin
          * (kunduzi oq, kechqurun qorong'i). Ikkalasi ham panelning o'ng
          * chekkasida — kuniga yuz marta bosiladigan tugmalar chapda.
          */}
        <ThemeSwitch />
        <LangSwitch />

        {user && (
          <div className="flex shrink-0 items-center gap-2">
            {/*
             * Avatar — HAR ekranda. Ism esa faqat `lg` dan.
             *
             * Telefonda ism 140 px yeydi va u kursni pastki qatorga
             * itarib yuborardi. Bosh harflar esa 32 px da bir xil
             * savolga javob beradi, `title` da to'liq ism qoladi.
             */}
            <span
              aria-hidden="true"
              /* Ism VA rol — pastdagi blok tor ekranda chizilmaydi, ya'ni
                 rol faqat shu yerdan bilinadi. */
              title={`${user.fullName} — ${roleCodes.map((role) => roleLabel(role, t)).join(', ')}`}
              /*
               * `size-8` EDI — qatordagi eng kichik element, yonidagi
               * chiqish tugmasidan 4 px past. Doira boshqa shakl bo'lgani
               * uchun bu farq ayniqsa ko'zga tashlanardi: kvadrat
               * tugmalar qatorida bir oz kichik doira «tushib qolgan»
               * ko'rinadi. Endi u ham 36 px va harflar `text-sm` —
               * ikki harf 32 px doirada `text-xs` da mayda edi.
               */
              className="flex size-9 shrink-0 items-center justify-center rounded-full bg-chrome-2 text-sm font-bold text-chrome-accent"
            >
              {initials(user.fullName)}
            </span>

            {/*
             * ⚠️ `lg:` EMAS, `2xl:` — sarlavhaga pol qo'yilgandan keyin.
             *
             * O'lchandi (1440 px, admin roli): o'ng blok 915 px joy olardi
             * va butun panelga 1160 px bor edi. Ya'ni sarlavhaga hech narsa
             * qolmasdi va panel IKKI QATORGA bo'linardi — har ekranda
             * qo'shimcha ~48 px.
             *
             * Ism va rol shu blokdagi eng keng qism (~190 px) va ayni paytda
             * eng kam kerakli: foydalanuvchi kim sifatida kirganini biladi.
             * Avatar QOLADI va `title` da to'liq ism ham, rol ham chiqadi;
             * chiqish tugmasi ham joyida. 1536 px dan keng ekranda esa
             * ikkalasi ham qaytadi — o'sha yerda joy haqiqatan bor.
             */}
            <div className="hidden min-w-0 2xl:block">
              <p className="max-w-[9rem] truncate text-sm leading-tight font-medium text-chrome-ink">
                {user.fullName}
              </p>
              {/*
               * ROL ko'rsatiladi, email emas.
               *
               * Email hech narsa tushuntirmaydi — foydalanuvchi kim sifatida
               * kirganini allaqachon biladi. Rol esa «nega men buni
               * ko'rmayapman?» savoliga BIRINCHI javob va u qo'llab-quvvatlashga
               * qilinadigan qo'ng'iroqlarning yarmini oldini oladi.
               */}
              <p className="max-w-[9rem] truncate text-xs leading-tight text-chrome-muted">
                {roleCodes.map((role) => roleLabel(role, t)).join(', ')}
              </p>
            </div>
          </div>
        )}

        {/*
          Chiqish — endi IKONKA.

          «Chiqish» so'zi uch tilda uch xil uzunlikda (`Chiqish`, `Выход`,
          `Sign out`) va u panelda 90–110 px egallardi — kuniga BIR marta,
          ish oxirida bosiladigan tugma uchun. Ikonka 36 px oladi, nom esa
          `aria-label` va `title` da to'liq qoladi: ekran o'quvchi ham,
          sichqonchali odam ham nomni oladi.

          Ichida «oxirgi ochilganlar» va filial tanlovi TOZALANADI.

          Ikkalasi ham `localStorage` da va foydalanuvchiga bog'lanmagan:
          bitta kompyuterda navbatma-navbat ishlaydigan ikki xodim
          bir-birining hujjat RAQAMLARINI ko'rardi va birinchisining filial
          tanlovi ikkinchisiga qolib ketardi — u boshqa filialning
          raqamlarini ko'rib O'Z filiali deb o'ylardi. Hujjatning o'zini
          ochib bo'lmaydi (server o'z ruxsatini tekshiradi), lekin raqam
          ham ma'lumot.
        */}
        <button
          type="button"
          aria-label={t('auth.logout')}
          title={t('auth.logout')}
          onClick={() => {
            /*
             * `user.id` beriladi — faqat CHIQAYOTGAN odamning ro'yxati
             * o'chadi. Argumentsiz chaqiruv shu brauzerdagi hammasini
             * o'chirardi va bir kompyuterda ishlaydigan hamkasb ertaga
             * o'z ro'yxatini bo'sh topardi (`search.ts` izohi).
             */
            clearRecents(user?.id);
            clearActiveBranch();
            logout();
          }}
          className="flex size-9 shrink-0 items-center justify-center rounded-lg text-chrome-muted transition-colors hover:bg-chrome-2 hover:text-chrome-danger motion-reduce:transition-none"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            className="size-5"
            aria-hidden="true"
          >
            <path
              d="M15 17l5-5-5-5M20 12H9M12 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </header>
  );
}
